import { defineStore } from 'pinia';
import { ref } from 'vue';
import axios from 'axios';
import type { LimitFields, TimeLimit, SelfExclusion } from '@/types';
import { getAuthHeaders } from '@/utils/getHeaders';
import { usePlayerStore } from './player';
import { useAuthStore } from './auth';

interface LimitFlagData {
    modal: boolean;
    value: number;
    start_date: string | null;
    end_date: string | null;
    usage: number;
}

interface LimitsModalState {
    depositFlag: LimitFlagData;
    wageringFlag: LimitFlagData;
    lossFlag: LimitFlagData;
    sessionFlag: boolean;
}

export const useLimitsStore = defineStore('limits', () => {
    const playerStore = usePlayerStore();
    const authStore = useAuthStore();
    const timerInterval = ref<NodeJS.Timeout | null>(null);
    const limitsModal = ref<LimitsModalState>({
        depositFlag: {
            modal: false,
            value: 0,
            start_date: null,
            end_date: null,
            usage: 0
        },
        wageringFlag: {
            modal: false,
            value: 0,
            start_date: null,
            end_date: null,
            usage: 0
        },
        lossFlag: {
            modal: false,
            value: 0,
            start_date: null,
            end_date: null,
            usage: 0
        },
        sessionFlag: false
    });
    function toSetTimerInterval(interval: NodeJS.Timeout | null) {
        timerInterval.value = interval;
    }
    function toSetLimitsData({ modal, value, start_date, end_date, usage }: { modal: boolean; value?: number; start_date?: string | null; end_date?: string | null; usage?: number }, type: keyof LimitsModalState) {
        if (type === 'sessionFlag') {
            limitsModal.value.sessionFlag = modal;
            return;
        }

        limitsModal.value[type] = {
            modal,
            value: value ?? 0,
            start_date: start_date ?? null,
            end_date: end_date ?? null,
            usage: usage ?? 0
        };
    }
    const awaitSetLimit = async (data: LimitFields, isDelete: boolean = false) => {
        let filteredData;
        if (isDelete) {
            if (data.isDepBlocked) {
                filteredData = {
                    balance_limit: null,
                    balance_limit_start_date: null,
                    balance_limit_end_date: null
                };
            } else if (data.isLossBlocked) {
                filteredData = {
                    loss_limit: null,
                    loss_limit_start_date: null,
                    loss_limit_end_date: null
                };
            } else if (data.isWagBlocked) {
                filteredData = {
                    wagering_limit: null,
                    wagering_limit_start_date: null,
                    wagering_limit_end_date: null
                };
            }
        } else {
            filteredData = Object.fromEntries(Object.entries(data).filter(([key]) => data[key as keyof typeof data] !== null && data[key as keyof typeof data] !== true && data[key as keyof typeof data] !== false));
        }
        const response = await axios.post('/main-api/users/me/set_limit', filteredData, getAuthHeaders());
        if (response.status === 200) {
            if (data.isDepBlocked) {
                playerStore.toUpdateLimit('deposit_limit_value', data.balance_limit);
                playerStore.toUpdateLimit('deposit_limit_start_date', data.balance_limit_start_date ? new Date(data.balance_limit_start_date).toISOString() : null);
                playerStore.toUpdateLimit('deposit_limit_end_date', data.balance_limit_end_date ? new Date(data.balance_limit_end_date).toISOString() : null);
            }
            if (data.isLossBlocked) {
                playerStore.toUpdateLimit('loss_limit_value', data.loss_limit);
                playerStore.toUpdateLimit('loss_limit_start_date', data.loss_limit_start_date ? new Date(data.loss_limit_start_date).toISOString() : null);
                playerStore.toUpdateLimit('loss_limit_end_date', data.loss_limit_end_date ? new Date(data.loss_limit_end_date).toISOString() : null);
            }
            if (data.isWagBlocked) {
                playerStore.toUpdateLimit('wagering_limit_value', data.wagering_limit);
                playerStore.toUpdateLimit('wagering_limit_start_date', data.wagering_limit_start_date ? new Date(data.wagering_limit_start_date).toISOString() : null);
                playerStore.toUpdateLimit('wagering_limit_end_date', data.wagering_limit_end_date ? new Date(data.wagering_limit_end_date).toISOString() : null);
            }
        }
    };
    const awaitSetTimeLimit = async (time: TimeLimit) => {
        const response = await axios.put('/main-api/users/me', time, getAuthHeaders());
        if (response.status === 200) {
            playerStore.toUpdatePlayerField('time_limit', time.time_limit);
        }
    };
    const awaitSetSelfExclusion = async (data: SelfExclusion) => {
        try {
            const response = await axios.post('/main-api/users/me/set_block', data, getAuthHeaders());
            if (response.status === 200) {
                authStore.signOut();
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    return { toSetTimerInterval, timerInterval, awaitSetLimit, awaitSetTimeLimit, awaitSetSelfExclusion, limitsModal, toSetLimitsData };
});
